/** Package constants organized in a plain object. */
export const Constants = {
  Name: 'Vertex Echo', // Product name.
  ContextAI: 'AI voice receptionist SaaS dashboard for small businesses', // AI translation context hint.
  BaseColor: '#0a0f11', // Base background color (landing page specific).

  // Portrait spacing boost: in portrait orientation the lobby multiplies the adaptive root font-size by this factor so every rem-based dimension (typography, padding, margins, gaps) scales up
  // uniformly when the device is held upright. Single source for the runtime wrapper (main.tsx), the pre-paint inline scripts (index.html / build.index.html), and the build-asset placeholder.
  PortraitSpacingScale: 1.5,

  // Founder names — non-translatable identifiers.
  FounderCTOName: 'Sotiri Karasoulos (Sotirios Carasoulos)',
  FounderCTOFirstName: 'Sotiri',
  FounderCPOName: 'Andy R. Mahase (Andy Rupnarine)',
  FounderCPOFirstName: 'Andy',

  // Public LinkedIn profile URLs — non-translatable, used on the About page.
  FounderCTOLinkedIn: 'https://www.linkedin.com/in/sotirik/',
  FounderCPOLinkedIn: 'https://www.linkedin.com/in/andyrup/',
  CompanyLinkedIn: 'https://linkedin.com/company/skar-ai/',

  // Internal route paths.
  Route: {
    Landing: '/', // Public marketing landing page (default).
    // About page tabs (public, no auth required).
    About: '/about',
    AboutDemo: '/about/demo',
    AboutCompany: '/about/company',
    AboutTerms: '/about/terms',
    AboutHelp: '/about/help',
    Home: '/dashboard', // Overview dashboard.
    // SaaS dashboard routes (Vertex Echo).
    Calls: '/dashboard/calls',
    CallDetail: '/dashboard/calls/:callID',
    Texts: '/dashboard/texts',
    TextDetail: '/dashboard/texts/:textID',
    Leads: '/dashboard/leads',
    Bookings: '/dashboard/bookings',
    Knowledge: '/dashboard/knowledge',
    KnowledgeNew: '/dashboard/knowledge/new',
    KnowledgeEdit: '/dashboard/knowledge/:documentID/edit',
    Profile: '/dashboard/profile', // Business Profile.
    Integrations: '/dashboard/integrations',
    Notifications: '/dashboard/notifications',
    Account: '/dashboard/account',
    Help: '/dashboard/help', // In-dashboard Help (the shared FAQ tab from the public About page).
    Billing: '/dashboard/billing',
    Invoices: '/dashboard/invoices',
    // Provider checkout return path. Stripe redirects here after hosted checkout (success/cancel); it resolves to `/billing` under every app basename (dev `/`, cloud `/web/apps/lobby`), where the
    // dashboard gate reconciles billing and forwards to the dashboard. Kept separate from `Billing` (the in-dashboard billing screen at `/dashboard/billing`).
    BillingReturn: '/billing'
  },

  // Route parameter keys.
  Param: {
    CallID: 'callID',
    TextID: 'textID',
    DocumentID: 'documentID'
  },

  // Asset bundle identifiers.
  Bundle: {
    Preload: 'preload',
    Lazy: 'lazy'
  },

  // Manifest identifier for this app.
  Manifest: 'apps/lobby',

  // Session-storage key for handing the landing signup email to the dashboard Create-account form.
  SignupPrefillStorageKey: 'vertex-echo.signup-prefill-email',

  // Query-param that forces the auth screen to open directly in Create-account (sign-up) view, e.g. `/dashboard?signup=1`. Used for a stable deep link to the signup screen (which carries the SMS
  // opt-in consent notice) - e.g. the exact URL provided to Twilio for A2P messaging registration. Any value (or bare `?signup`) triggers it.
  SignupQueryParam: 'signup',

  // Session-storage key for the live, shared landing/onboarding signup draft. Both SignupCard and AccountStep read and write this so edits survive navigation between the landing page and the
  // onboarding wizard. The server-side signup session is only updated on explicit user submit.
  SignupDraftStorageKey: 'vertex-echo.signup-draft',

  // Session-storage key for handing the plan selected during onboarding to the billing-setup gate (which starts the Stripe checkout for that plan).
  SetupBillingPlanStorageKey: 'vertex-echo.setup-billing-plan',

  // Session-storage key for handing the country selected during onboarding to the billing-setup gate, so the price summary renders in the tenant's billing currency before the subscription exists.
  SetupBillingCountryStorageKey: 'vertex-echo.setup-billing-country',

  // Session-storage key for handing the optional preferred area codes from the account step to the phone step (the voice-provider number hint). Absent on a cold resume is acceptable — the number is
  // then provisioned without a preferred area code.
  OnboardingAreaCodeStorageKey: 'vertex-echo.onboarding-area-code',

  // Theme CSS class suffix.
  ThemeSuffix: '-theme',

  // Middot glyph used as the inline divider between items
  Separator: '·'
} as const;
